import { useNavigate, Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { quotesApi } from "@/lib/quotes";
import { Button } from "@/components/ui/button";
import { ArrowRightCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

export type QuoteConvertButtonProps = {
  quoteId: number;
  status: string;
  convertedToJobId: number | null;
  canConvert: boolean;
};

export function QuoteConvertButton({ quoteId, status, convertedToJobId, canConvert }: QuoteConvertButtonProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => quotesApi.convertToJob(quoteId),
    onSuccess: (result) => {
      toast.success(
        result.alreadyConverted ? "Quote already converted" : "Quote converted",
        { description: `Job ${result.jobReferenceNo}` },
      );
      void queryClient.invalidateQueries({ queryKey: ["quote", quoteId] });
      void queryClient.invalidateQueries({ queryKey: ["quotes"] });
      navigate(`/jobs/${result.jobId}`);
    },
    onError: (err: Error) => {
      toast.error("Could not convert quote", { description: err.message });
    },
  });

  if (convertedToJobId !== null) {
    return (
      <Link to={`/jobs/${convertedToJobId}`} className="text-sm underline underline-offset-2">
        Go to Job
      </Link>
    );
  }

  if (status !== "Approved" || !canConvert) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full h-8 w-8 border-slate-300 text-blue-600 hover:bg-blue-50"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          <ArrowRightCircle className="h-4 w-4" />
          <span className="sr-only">Convert to Job</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>Convert to Job</TooltipContent>
    </Tooltip>
  );
}
